'use client';

/**
 * Custom reCAPTCHA Provider - Prevents Duplicate Script Loading
 *
 * CRITICAL FIX: The react-google-recaptcha-v3 library has a bug in Next.js 16
 * that causes the reCAPTCHA script to load 6-7 times (120 KB duplicate JS).
 *
 * This custom implementation:
 * 1. Loads reCAPTCHA script ONCE using Next.js Script component
 * 2. Provides grecaptcha instance via React Context
 * 3. Prevents duplicate script tags
 *
 * Performance impact:
 * - Saves ~120 KB of duplicate JavaScript
 * - Reduces TBT by preventing multiple script evaluations
 * - Improves Speed Index by loading script only once
 */

import { createContext, useContext, useState } from 'react';
import Script from 'next/script';
import { logger } from '@/lib/platform/utils/logger';

interface RecaptchaContextType {
  executeRecaptcha: ((action: string) => Promise<string>) | null;
  isLoaded: boolean;
}

const RecaptchaContext = createContext<RecaptchaContextType>({
  executeRecaptcha: null,
  isLoaded: false,
});

export const useRecaptcha = () => useContext(RecaptchaContext);

interface RecaptchaProviderProps {
  children: React.ReactNode;
  reCaptchaKey: string;
  theme?: 'light' | 'dark';
}

export function RecaptchaProvider({ children, reCaptchaKey }: RecaptchaProviderProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [grecaptcha, setGrecaptcha] = useState<ReCaptchaInstance | null>(null);

  if (!reCaptchaKey) {
    logger.warn('[reCAPTCHA] Site key not configured, reCAPTCHA disabled');
    return <>{children}</>;
  }

  const handleScriptLoad = () => {
    if (window.grecaptcha && window.grecaptcha.ready) {
      window.grecaptcha.ready(() => {
        setGrecaptcha(window.grecaptcha);
        setIsLoaded(true);
        logger.log('[reCAPTCHA] Loaded successfully (single instance)');
      });
    }
  };

  const executeRecaptcha = async (action: string): Promise<string> => {
    if (!grecaptcha || !isLoaded) {
      logger.warn('[reCAPTCHA] Not loaded yet, returning empty token');
      return '';
    }

    try {
      const token = await grecaptcha.execute(reCaptchaKey, { action });
      return token;
    } catch (error) {
      logger.error('[reCAPTCHA] Execute failed:', error);
      return '';
    }
  };

  return (
    <>
      {/* Hide reCAPTCHA badge completely (we show disclaimer text instead) */}
      <style jsx global>{`
        .grecaptcha-badge {
          visibility: hidden !important;
          display: none !important;
          opacity: 0 !important;
        }
      `}</style>

      {/* Load reCAPTCHA script ONCE - prevents duplicate loads */}
      <Script
        id="recaptcha-script"
        src={`https://www.google.com/recaptcha/api.js?render=${reCaptchaKey}`}
        onLoad={handleScriptLoad}
        strategy="afterInteractive"
      />

      <RecaptchaContext.Provider
        value={{ executeRecaptcha: isLoaded ? executeRecaptcha : null, isLoaded }}
      >
        {children}
      </RecaptchaContext.Provider>
    </>
  );
}

// TypeScript types for grecaptcha
interface ReCaptchaInstance {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha: ReCaptchaInstance;
  }
}
